import { useEffect } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { useOrderFormStoreActions } from '@/modules/order/stores/order-form.store'
import { useMainStore } from '@/modules/shared/stores/main.store'

import {
  borders,
  categories,
  colors,
  profileProperties,
} from '../constants/customer-profile-report.constant'
import { useCustomerProfileData } from '../data/customer-profile.data'
import { useCustomerFormStore } from '../stores/customer-form.store'
import { useCustomerProfileStore } from '../stores/customer-profile.store'
import { useStartOrderByCustomer } from './use-start-order-by-customer.hook'

export const useCustomerProfile = () => {
  const navigate = useNavigate()
  const location = useLocation()

  const { setInitialLocationForOrder } = useMainStore().actions

  const startOrderOfClientHook = useStartOrderByCustomer()

  const { handleGetCustomerProfile, isLoading } = useCustomerProfileData()

  const { dataProfile } = useCustomerProfileStore()
  const { setDataProfile } = useCustomerProfileStore().actions

  const { clientSelected } = useCustomerFormStore()

  const { setOperationType } = useOrderFormStoreActions()

  const handleBackPage = () => {
    navigate(-1)
    setDataProfile(undefined)
  }

  const chartDataProfile = {
    labels: [],
    data: [],
    backgroundColor: [],
    borderColor: [],
  }

  profileProperties.forEach((property, index) => {
    if (dataProfile?.perfil?.[property] > 0) {
      chartDataProfile.labels.push(categories[index])
      chartDataProfile.data.push(dataProfile?.perfil?.[property])
      chartDataProfile.backgroundColor.push(colors[index])
      chartDataProfile.borderColor.push(borders[index])
    }
  })

  const productData = {
    labels: dataProfile?.produtos.map((item) => item.produto) || [],
    options:
      dataProfile?.produtos.length > 5
        ? {
            maintainAspectRatio: false,
          }
        : {},
    datasets: [
      {
        label: 'Porcentagem',
        axis: 'y',
        data: dataProfile?.produtos.map((item) => item.porcentagem) || [],
        backgroundColor: dataProfile?.produtos.map(() => colors[0]),
        borderColor: dataProfile?.produtos.map(() => borders[0]),
        borderWidth: 1,
      },
    ],
  }

  const dataOfFamilyProfile = {
    labels: dataProfile?.familias.map((item) => item.descricao) || [],
    options:
      dataProfile?.familias.length > 5 ? { maintainAspectRatio: false } : {},
    datasets: [
      {
        label: 'Porcentagem',
        data: dataProfile?.familias.map((item) => item.porcentagem) || [],
        backgroundColor: dataProfile?.familias.map((_, index) => colors[index]),
        borderColor: dataProfile?.familias.map(() => borders[0]),
        borderWidth: 1,
      },
    ],
  }

  const dataOfReportProfile = {
    labels: chartDataProfile.labels,
    options: {
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom' as const,
        },
      },
    },
    datasets: [
      {
        label: '(%) Perfil Pgto',
        data: chartDataProfile.data,
        backgroundColor: chartDataProfile.backgroundColor,
        borderColor: chartDataProfile.borderColor,
        borderWidth: 1,
      },
    ],
  }

  const { setOrderSelected } = useOrderFormStoreActions()

  const handleNavigationOrderToVisualization = (orderId: number) => {
    setInitialLocationForOrder(location.pathname)

    const orderSelected = dataProfile?.pedidos?.find(
      (item) => item.idPedidoCel === orderId,
    )
    setOrderSelected(orderSelected)

    setOperationType('view')
    navigate(`/pedido/view/${orderId}`)
  }

  const handleStartOrder = () => {
    startOrderOfClientHook.action.handleStartOrder()
  }

  const { id } = useParams()

  useEffect(() => {
    if (dataProfile === undefined && (clientSelected?.idEntidade || id)) {
      handleGetCustomerProfile({
        payload: {
          idClient: clientSelected?.idEntidade.toString() ?? id,
        },
      })
    }

    return () => {
      setDataProfile(undefined)
    }
  }, [])

  return {
    dataProfile,
    clientSelected,
    dataOfReportProfile,
    isLoading,
    handleBackPage,
    handleStartOrder,
    handleNavigationOrderToVisualization,
    dataOfFamilyProfile,
    productData,
  }
}
